import { isTrackReady, splitRoles, migrateInstrument } from '../helpers';

const TRACK_SLOTS = [
  { label: 'Voce',     icon: '/voice.png',  keywords: ['voce'] },
  { label: 'Piano/Keys', icon: '/keys.png',   keywords: ['tasti','piano','keys','organ'] },
  { label: 'Chitarra', icon: '/guitar.png', keywords: ['chitar','guitar'] },
  { label: 'Basso',    icon: '/bass.png',   keywords: ['basso','bass'] },
  { label: 'Batteria', icon: '/drums.png',  keywords: ['batter','drum','percuss'] },
];

const NO_BORDER = '1.5px solid transparent';

const roleMatchesSlot = (role, slot) => {
  const r = role.toLowerCase().trim();
  if (slot.keywords.length === 0) {
    return !TRACK_SLOTS.slice(0, -1).some(s => s.keywords.some(k => r.includes(k)));
  }
  return slot.keywords.some(k => r.includes(k));
};

function SetlistItem({ track, currentUser, onJoin, onLeave, isParticipating, onEdit, score }) {
  const players = track.track_players || [];
  const myEntry = players.find(p => p.musician_id === currentUser.id);

  // Default slots
  const defaultSlots = new Set();
  if (currentUser.default_sings) defaultSlots.add('Voce');
  if (currentUser.default_instrument) {
    const m = migrateInstrument(currentUser.default_instrument);
    if (m) defaultSlots.add(m);
  }
  if (defaultSlots.size === 0) {
    if (currentUser.sings) defaultSlots.add('Voce');
    if ((currentUser.instruments || []).length > 0) {
      const m = migrateInstrument(currentUser.instruments[0]);
      if (m) defaultSlots.add(m);
    }
  }

  // Secondary slots
  const secondarySlots = new Set();
  if (currentUser.sings && !defaultSlots.has('Voce')) secondarySlots.add('Voce');
  (currentUser.instruments || []).forEach(i => {
    const m = migrateInstrument(i);
    if (m && !defaultSlots.has(m)) secondarySlots.add(m);
  });

  const canInteract = currentUser.accountType === 'musician' && isParticipating;

  // Per-slot coverage — always show all 6 slots
  const slotInfo = TRACK_SLOTS.map(slot => {
    const covering = players.filter(p =>
      splitRoles(p.role.replace('(alt)', '')).some(r => roleMatchesSlot(r, slot))
    );
    const isDefault   = defaultSlots.has(slot.label);
    const isSecondary = secondarySlots.has(slot.label);
    return {
      slot,
      iAmIn:           covering.some(p => p.musician_id === currentUser.id),
      coveredByOthers: covering.some(p => p.musician_id !== currentUser.id),
      isDefault,
      isSecondary,
    };
  });

  return (
    <>
      <div className={`setlist-item ${isTrackReady(track) ? 'ready' : 'draft'}`}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>

        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6,
          overflow: 'hidden', whiteSpace: 'nowrap',
          cursor: onEdit ? 'pointer' : 'default' }}
          onClick={() => onEdit && onEdit(track)}>
          <span style={{ fontWeight: 700, fontSize: 18, color: 'var(--text)', flexShrink: 0 }}>
            {track.title}
          </span>
          {track.key && (
            <span style={{ flexShrink: 0, fontSize: 16, color: 'var(--muted)', fontWeight: 500 }}>
              ({track.key})
            </span>
          )}
          {track.artist && (
            <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
              fontSize: 17, color: 'var(--muted)' }}>
              – {track.artist}
            </span>
          )}
          {score && (
            <span style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 19, fontWeight: 800, lineHeight: 1,
              color: score.engagement < 0.7 ? '#FFA726' : score.coverage >= 4 ? '#ED4D1D' : 'var(--muted)' }}>
              {Math.round(score.score)}
            </span>
          )}
        </div>

        {/* Instrument icons */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {slotInfo.map(({ slot, iAmIn, coveredByOthers, isDefault, isSecondary }) => {
            const canJoin = canInteract && !myEntry;
            let bg, border, imgFilter, cursor, onClick;

            if (iAmIn) {
              // State 5: I'm in — orange bg, black icon, tap to leave
              bg        = '#ED4D1D';
              border    = '1.5px solid #ED4D1D';
              imgFilter = 'none';
              cursor    = canInteract ? 'pointer' : 'default';
              onClick   = canInteract ? () => onLeave(track.id) : null;

            } else if (coveredByOthers) {
              // States 1, 4, 7: booked by others — white bg, black icon
              bg        = 'rgba(255,255,255,0.92)';
              border    = '2px solid rgba(80,80,80,0.7)';
              imgFilter = 'none';
              cursor    = 'default';
              onClick   = null;

            } else if ((isDefault || isSecondary) && canJoin) {
              // My instrument, slot free, not yet in track — dashed orange border
              bg        = 'transparent';
              border    = '2px dashed #ED4D1D';
              imgFilter = 'invert(1)';
              cursor    = 'pointer';
              onClick   = () => onJoin(track.id, slot.label, 'originale', false);

            } else {
              // Not mine, free — faint bg, dim icon
              bg        = 'rgba(255,255,255,0.05)';
              border    = NO_BORDER;
              imgFilter = 'invert(1) opacity(0.3)';
              cursor    = 'default';
              onClick   = null;
            }

            return (
              <div key={slot.label} title={slot.label}
                onClick={onClick || undefined}
                style={{
                  position: 'relative', width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: bg, border, cursor, userSelect: 'none',
                }}>
                <img src={slot.icon} alt={slot.label}
                  style={{ width: 34, height: 34, objectFit: 'contain',
                    filter: imgFilter, pointerEvents: 'none' }} />
              </div>
            );
          })}
        </div>
      </div>

    </>
  );
}

export default SetlistItem;
